import { useEffect, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useToast } from '@/hooks/use-toast';
import { getAccessToken } from '@/lib/api/axios';
import { getEnv } from '@/lib/env';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Global hook that subscribes to tool_watch WebSocket events and shows:
 *   1. An in-app toast (always)
 *   2. A browser Notification via the Notification API (when permission is granted
 *      and the user is NOT already viewing that contact)
 *
 * Mount this once in Layout so every logged-in user receives alerts regardless
 * of which page they are on.
 */
export function useGlobalToolWatchNotifications() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isClientView = useAuthStore((s) => s.user?.role === 'client');

  // Use refs so the WebSocket effect doesn't re-run on every navigation or toast change.
  const toastRef = useRef(toast);
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location.pathname);

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  // Request browser notification permission on mount (non-blocking).
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (isClientView) return;

    const MAX_RECONNECT_ATTEMPTS = 5;
    const BASE_DELAY = 1000;
    const MAX_DELAY = 16000;
    let reconnectAttempts = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let ws: WebSocket | null = null;

    function connect() {
      const token = getAccessToken();
      if (!token) return;

      const apiUrl = getEnv('VITE_API_URL');
      const apiOrigin = new URL(apiUrl);
      const wsProtocol = apiOrigin.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${apiOrigin.host}/listen-ws/${encodeURIComponent(token)}`;

      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        reconnectAttempts = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data as string);
          if (message.type === 'tool_watch') {
            handleToolWatch(message);
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        ws = null;
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(BASE_DELAY * Math.pow(2, reconnectAttempts), MAX_DELAY);
          reconnectAttempts++;
          reconnectTimer = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        ws?.close();
      };
    }

    function handleToolWatch(message: {
      toolName?: string;
      contactId?: string;
      contactName?: string;
      argumentsSummary?: string;
      timestamp?: string;
    }) {
      const toolName = String(message.toolName ?? 'tool');
      const contactName = message.contactName ? String(message.contactName) : undefined;
      const argsSummary = message.argumentsSummary ? String(message.argumentsSummary) : undefined;

      // Always show in-app toast.
      toastRef.current({
        title: `Tool "${toolName}" invoked`,
        description: contactName
          ? `During the conversation with ${contactName}.`
          : 'During an active conversation.',
      });

      // Browser notification — skip if the user is already viewing that contact.
      const contactId = message.contactId ? String(message.contactId) : undefined;
      const isViewingContact = contactId && locationRef.current === `/sales-console/${contactId}`;

      if (!isViewingContact && 'Notification' in window && Notification.permission === 'granted') {
        const body = contactName
          ? `Contact: ${contactName}${argsSummary ? ` — ${argsSummary}` : ''}`
          : 'During an active conversation.';

        const notification = new Notification(`Tool Alert: ${toolName}`, {
          body,
          icon: '/logo.png',
        });

        notification.onclick = () => {
          window.focus();
          if (contactId) {
            navigateRef.current(`/sales-console/${contactId}`);
          }
          notification.close();
        };
      }
    }

    connect();

    return () => {
      clearTimeout(reconnectTimer);
      if (ws) {
        ws.onclose = null;
        ws.close();
        ws = null;
      }
    };
  }, [isClientView]);
}
